export interface TemplatePageDef {
  slug: string;
  title: string;
  content: string;
}

export interface SpaceTemplate {
  id: string;
  label: string;
  icon: string;
  description: string;
  agentInstructions: string;
  pages: TemplatePageDef[];
}

export const SPACE_TEMPLATES: SpaceTemplate[] = [
  {
    id: "blank",
    label: "Blank",
    icon: "📄",
    description: "Start from scratch",
    agentInstructions: "",
    pages: [],
  },
  {
    id: "engineering",
    label: "Engineering Team",
    icon: "⚙️",
    description: "Architecture, decisions, runbooks",
    agentInstructions:
      "This space documents engineering knowledge.\n" +
      "- README: team context and ownership areas\n" +
      "- Architecture: system design and components (update after significant changes)\n" +
      "- Decisions: use ADR format (Status / Context / Decision / Consequences)\n" +
      "- Runbooks: numbered steps, be precise and operational\n\n" +
      "Always link related pages. Use tables for structured data.",
    pages: [
      {
        slug: "readme",
        title: "Team Overview",
        content:
          "# Team Overview\n\n" +
          "Overview of the engineering team, responsibilities, and how to work with us.\n\n" +
          "## Team Members\n\n" +
          "*List team members and their areas of ownership here.*\n\n" +
          "## How to Use This Wiki\n\n" +
          "- **[Architecture](/architecture)** — System design and component documentation\n" +
          "- **[Decisions](/decisions)** — Technical decisions in ADR format\n" +
          "- **[Runbooks](/runbooks)** — Step-by-step operational procedures\n",
      },
      {
        slug: "architecture",
        title: "Architecture",
        content:
          "# System Architecture\n\n" +
          "## Overview\n\n" +
          "*Describe the high-level system architecture here.*\n\n" +
          "## Components\n\n" +
          "| Component | Technology | Purpose | Owner |\n" +
          "|-----------|------------|---------|-------|\n" +
          "| | | | |\n\n" +
          "## Data Flow\n\n" +
          "*Describe how data moves through the system.*\n\n" +
          "## Key Decisions\n\n" +
          "See the [Decision Log](/decisions) for rationale behind architectural choices.\n",
      },
      {
        slug: "decisions",
        title: "Decision Log",
        content:
          "# Decision Log\n\n" +
          "Technical decisions in ADR (Architecture Decision Record) format.\n\n" +
          "## Template\n\n" +
          "### [YYYY-MM-DD] Decision Title\n\n" +
          "- **Status**: proposed | accepted | deprecated | superseded\n" +
          "- **Context**: What situation required a decision?\n" +
          "- **Decision**: What was decided?\n" +
          "- **Consequences**: What are the trade-offs?\n\n" +
          "---\n\n" +
          "*Add decisions below, newest first.*\n",
      },
      {
        slug: "runbooks",
        title: "Runbooks",
        content:
          "# Runbooks\n\n" +
          "Step-by-step procedures for operational tasks.\n\n" +
          "## Deployment\n\n" +
          "*Add deployment procedures here.*\n\n" +
          "## Incident Response\n\n" +
          "1. Identify the scope of impact\n" +
          "2. Check relevant dashboards and logs\n" +
          "3. Page on-call if P0/P1\n" +
          "4. Document the incident timeline\n\n" +
          "## Common Issues\n\n" +
          "*Add troubleshooting guides for recurring issues.*\n",
      },
    ],
  },
  {
    id: "product",
    label: "Product Wiki",
    icon: "📋",
    description: "Roadmap, decisions, glossary",
    agentInstructions:
      "This space documents product decisions and definitions.\n" +
      "- Roadmap: keep it high-level, update when priorities change\n" +
      "- Glossary: always expand when new terms are introduced — this is the most critical page\n\n" +
      "Use plain language. Avoid jargon that isn't defined in the glossary.",
    pages: [
      {
        slug: "readme",
        title: "Product Overview",
        content:
          "# Product Overview\n\n" +
          "*Describe the product, its purpose, and target users.*\n\n" +
          "## Vision\n\n" +
          "*One sentence describing where the product is headed.*\n\n" +
          "## Key Sections\n\n" +
          "- **[Roadmap](/roadmap)** — Current and upcoming features\n" +
          "- **[Glossary](/glossary)** — Definitions of domain-specific terms\n",
      },
      {
        slug: "roadmap",
        title: "Roadmap",
        content:
          "# Roadmap\n\n" +
          "## Now\n\n" +
          "*What is actively being built.*\n\n" +
          "## Next\n\n" +
          "*What is planned for the next cycle.*\n\n" +
          "## Later\n\n" +
          "*Future ideas and backlog.*\n\n" +
          "---\n\n" +
          "*Last updated: [date]*\n",
      },
      {
        slug: "glossary",
        title: "Glossary",
        content:
          "# Glossary\n\n" +
          "Domain-specific terms and definitions. " +
          "This is the most important reference page for AI agents working in this space.\n\n" +
          "| Term | Definition |\n" +
          "|------|------------|\n" +
          "| | |\n\n" +
          "## Why This Matters\n\n" +
          "Consistent terminology reduces miscommunication between team members and AI agents.\n",
      },
    ],
  },
  {
    id: "personal",
    label: "Personal KB",
    icon: "📚",
    description: "Notes, projects, references",
    agentInstructions:
      "This is a personal knowledge base. Write in first person, conversational style.\n" +
      "- Projects: track status changes and milestones\n" +
      "- Notes: capture ideas and observations with dates\n\n" +
      "Keep entries concise. Flag items that need follow-up with [TODO].",
    pages: [
      {
        slug: "readme",
        title: "About This Knowledge Base",
        content:
          "# About This Knowledge Base\n\n" +
          "A personal wiki for notes, projects, and ideas — maintained with the help of AI agents.\n\n" +
          "## How This Works\n\n" +
          "- Agents write drafts and I review them\n" +
          "- Pages are published after my approval\n" +
          "- Everything here is searchable and linkable\n\n" +
          "## Sections\n\n" +
          "- **[Projects](/projects)** — Active and completed projects\n" +
          "- **[Notes](/notes)** — Running log of observations and ideas\n",
      },
      {
        slug: "projects",
        title: "Projects",
        content:
          "# Projects\n\n" +
          "## Active\n\n" +
          "| Project | Goal | Status |\n" +
          "|---------|------|--------|\n" +
          "| | | |\n\n" +
          "## Completed\n\n" +
          "*Archive completed projects here.*\n\n" +
          "## Ideas\n\n" +
          "*Projects I might start someday.*\n",
      },
      {
        slug: "notes",
        title: "Notes",
        content:
          "# Notes\n\n" +
          "A running log of observations, ideas, and miscellaneous notes.\n\n" +
          "---\n\n" +
          "*Add notes below, newest first.*\n",
      },
    ],
  },
];
